import styles from './index.module.css'

import { LuChevronDown } from '@react-icons/all-files/lu/LuChevronDown'
import { LuPlus } from '@react-icons/all-files/lu/LuPlus'
import { LuSlidersHorizontal } from '@react-icons/all-files/lu/LuSlidersHorizontal'
import { LuSparkles } from '@react-icons/all-files/lu/LuSparkles'
import { LuThumbsDown } from '@react-icons/all-files/lu/LuThumbsDown'
import { LuThumbsUp } from '@react-icons/all-files/lu/LuThumbsUp'
import cn from 'clsx'
import groupBy from 'lodash/groupBy'
import uniq from 'lodash/uniq'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import { removeNativeTagFromTracks } from '@disco/state-management/actions/PlaylistTrackActions'
import { updateSearchParams } from '@disco/state-management/actions/SearchActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'

import type {
  NativeCategory,
  NativeTag,
  NativeTagWithCategory,
  Track,
} from 'types/track'

import NativeTagsPopover from 'components/common/native-tags-popover'
import {
  Accordion,
  Button,
  Flex,
  Heading,
  IconButton,
  Text,
  Tooltip,
} from 'components/ds'
import Tag from 'components/ui/tag'

import { useDispatch, useSelector } from 'hooks/use-redux'

import {
  type CategoryAccent,
  getFallbackCategoryMeta,
  getKnownCategoryMeta,
} from './category-meta'

/** Single accordion value — the whole Tags body collapses as one unit. */
const TAGS_SECTION = 'tags'

const ACCENT_CLASS: Record<CategoryAccent, string> = {
  ruby: styles.accentRuby,
  amber: styles.accentAmber,
  violet: styles.accentViolet,
  teal: styles.accentTeal,
  blue: styles.accentBlue,
  gray: styles.accentGray,
  green: styles.accentGreen,
  orange: styles.accentOrange,
  indigo: styles.accentIndigo,
  pink: styles.accentPink,
}

type Props = {
  track: Track
  readOnly?: boolean
  /** Flat track metadata (Key, ISRC, Released, etc.) rendered in its own
   * card below the tags, always expanded (not part of the accordion). */
  metadataContent?: React.ReactNode
}

/**
 * Native tags pane component that displays track tags grouped by category
 * Shows tags with color indicators, handles tag clicks for search, and tag removal
 * Integrates with Mixpanel for analytics and supports read-only modes
 * @param track - Track object containing native tags to display
 * @param readOnly - Whether the pane should be in read-only mode
 */
const NativeTagsPane = (props: Props): null | React.ReactElement<'div'> => {
  const dispatch = useDispatch()
  const isPhone = useSelector(isDeviceTypePhone)
  const tagsByCat = groupBy(props.track.native_tags, 'category_id')
  const colorIndicatorsEnabled = useFeatureEnabled(COLOR_INDICATORS)
  const isNativeTagsReadonly: boolean = Boolean(
    useSelector(
      state => state.user.business?.private_metadata.is_native_tags_readonly,
    ) || props.readOnly,
  )
  const nativeCategories: {
    [key in NativeCategory['id']]?: NativeCategory
  } = useSelector(state => state.entities.nativeCategories)
  const search = useSelector(state => state.search)
  // UI-only signal — no backend or analytics event exists yet to persist tag-accuracy feedback.
  const [vote, setVote] = useState<'down' | 'up' | null>(null)
  const [isAddTagOpen, setIsAddTagOpen] = useState(false)

  const handleTagClick = (tag: NativeTag) => () => {
    const params = {
      nativeTags: uniq(search.nativeTags.concat(tag.id)),
      searchOpen: true,
    }
    mixpanel.sendPureEvent('Clicking on track tag (faceted & track pane)', {
      Tags: [tag.name],
      Type: 'Track pane',
    })
    dispatch(updateSearchParams(params))
  }

  const handleTagRemove = (tag: NativeTag) => () => {
    dispatch(removeNativeTagFromTracks([props.track.id], tag))
  }

  if (isPhone && props.track.native_tags?.length === 0) return null

  const categoryIds = Object.keys(tagsByCat).map(Number)
  let fallbackIndex = 0

  return (
    <div className={styles.pane} data-testid="native-tags-pane">
      <Accordion.Root
        key={props.track.id}
        className={styles.card}
        type="single"
        collapsible
        defaultValue={TAGS_SECTION}
      >
        <Accordion.Item value={TAGS_SECTION} className={styles.tagsSection}>
          <Flex
            align="center"
            justify="between"
            px="4"
            pt="4"
            pb="3"
            className={styles.sectionHeader}
          >
            <Heading size="4" weight="medium">
              Tags
            </Heading>
            <Flex align="center" gap="3">
              {!props.readOnly && (
                <Button
                  variant="surface"
                  color="gray"
                  size="2"
                  radius="full"
                  disabled={isNativeTagsReadonly}
                  onClick={() => setIsAddTagOpen(true)}
                >
                  <LuPlus size={15} />
                  Add tag
                </Button>
              )}
              {/* Radix wants the trigger inside a Header for a11y; it's
                  reset to a bare flex item so the chevron lines up with
                  the Add tag button instead of forming its own row. */}
              <Accordion.Header className={styles.sectionHeaderTrigger}>
                <Accordion.Trigger asChild>
                  <IconButton
                    variant="ghost"
                    color="gray"
                    size="2"
                    aria-label="Show or hide tags"
                  >
                    <LuChevronDown
                      className={styles.sectionChevron}
                      size={18}
                    />
                  </IconButton>
                </Accordion.Trigger>
              </Accordion.Header>
            </Flex>
          </Flex>
          {isAddTagOpen && (
            <NativeTagsPopover
              trackIds={[props.track.id]}
              // Only `.id`/`.name` are read off assignedTags here (see
              // native-tags-popover's mapStateToProps) so the missing
              // `.category` field from NativeTag is never actually accessed.
              assignedTags={
                (props.track.native_tags || []) as NativeTagWithCategory[]
              }
              onClose={() => setIsAddTagOpen(false)}
            />
          )}
          <Accordion.Content className={styles.sectionContent}>
            <div className={styles.categoryList}>
              {categoryIds.map((id: NativeCategory['id']) => {
                const category = nativeCategories[id]
                const tags = tagsByCat[id] ?? []
                const meta =
                  getKnownCategoryMeta(category?.name) ??
                  getFallbackCategoryMeta(fallbackIndex++)
                const Icon = meta.icon
                const accentClass = ACCENT_CLASS[meta.accent]

                return (
                  <div key={id} className={styles.categoryRow}>
                    {/* The icon replaces a written category label, so it
                        carries the name for hover and assistive tech. */}
                    {category?.name ? (
                      <Tooltip content={category.name}>
                        <div
                          className={cn(styles.categoryIcon, accentClass)}
                          role="img"
                          aria-label={category.name}
                          data-testid="native-tags-category"
                        >
                          <Icon size={18} />
                        </div>
                      </Tooltip>
                    ) : (
                      <div
                        className={cn(styles.categoryIcon, accentClass)}
                        aria-hidden
                      >
                        <Icon size={18} />
                      </div>
                    )}
                    <div className={styles.categoryContentInner}>
                      {tags.map((tag: NativeTag) => (
                        <Tag
                          key={tag.id}
                          tag={tag.name}
                          variant="surface"
                          tagColor={
                            colorIndicatorsEnabled ? tag.color : undefined
                          }
                          onTagClick={isPhone ? undefined : handleTagClick(tag)}
                          onRemoveClick={
                            isNativeTagsReadonly ||
                            isPhone ||
                            props.track?.read_only
                              ? undefined
                              : handleTagRemove(tag)
                          }
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
            {!props.readOnly && (
              <Flex
                align="center"
                justify="between"
                px="4"
                py="3"
                className={styles.feedbackFooter}
              >
                <Flex align="center" gap="2">
                  <LuSparkles size={14} className={styles.feedbackIcon} />
                  <Text size="2" color="gray">
                    Were these tags helpful?
                  </Text>
                </Flex>
                <Flex gap="3" role="group" aria-label="Rate tag accuracy">
                  <Tooltip content="AI tags look right">
                    <IconButton
                      variant="ghost"
                      size="1"
                      color={vote === 'up' ? 'teal' : 'gray'}
                      aria-label="AI tags look right"
                      aria-pressed={vote === 'up'}
                      onClick={() => setVote(v => (v === 'up' ? null : 'up'))}
                    >
                      <LuThumbsUp size={15} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip content="AI tags look wrong">
                    <IconButton
                      variant="ghost"
                      size="1"
                      color={vote === 'down' ? 'ruby' : 'gray'}
                      aria-label="AI tags look wrong"
                      aria-pressed={vote === 'down'}
                      onClick={() =>
                        setVote(v => (v === 'down' ? null : 'down'))
                      }
                    >
                      <LuThumbsDown size={15} />
                    </IconButton>
                  </Tooltip>
                </Flex>
              </Flex>
            )}
          </Accordion.Content>
        </Accordion.Item>
      </Accordion.Root>
      {props.metadataContent && (
        <div className={styles.card}>
          <Flex
            align="center"
            justify="between"
            px="4"
            pt="4"
            pb="3"
            className={styles.sectionHeader}
          >
            <Heading size="4" weight="medium">
              Metadata
            </Heading>
            <Tooltip content="Choose displayed fields">
              <IconButton asChild variant="ghost" size="1" color="gray">
                <Link
                  to="/settings/display"
                  aria-label="Choose displayed metadata fields"
                >
                  <LuSlidersHorizontal size={14} />
                </Link>
              </IconButton>
            </Tooltip>
          </Flex>
          <div className={styles.metadataContentInner}>
            {props.metadataContent}
          </div>
        </div>
      )}
    </div>
  )
}

export default NativeTagsPane
